import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PersistentMessage } from '../Types/Notifications.types';
import { HubConnection } from '@microsoft/signalr';
import { AppDispatch, RootState } from '../../app/store';
import { downloadAutoReviewReportByCalculationId, downloadErrorByCalculationId, downloadReportByCalculationId, getNotificationsByJobId, getNotificationsByUser, getNotificationsByEventGroup } from '../../api/GlobalActions/GlobalActions.api';
import { currDateAndTime, saveStream } from '../Utils/utils';
import { showErrorSnackbar, showSuccessSnackbar } from '../../component/snackbar/Snackbar.slice';
import { Messages } from '../../Constants/Messages';
import { DownloadCalculationAutoReviewReports, DownloadCalculationReports } from '../Types/Calculation.types';
import { Guid } from 'guid-typescript';

export type setIsStandardReportEnabledPayload = {
	hierarchyLevelId: string;
	isStandardReportEnabled: boolean;
};

type State = {
	isNewNotification: number;
	isSignalRConnected: boolean;
	currentGroup: string[];
	isStandardReportEnabled: boolean;
	id?: string;
	persistentMessage: PersistentMessage[];
};

const initialState: State = {
	isNewNotification: 0,
	isSignalRConnected: false,
	currentGroup: [],
	isStandardReportEnabled: false,
	id: '',
	persistentMessage: [],
};

export const signalRConnected = createAsyncThunk<
	void,
	{ hubConnection: HubConnection },
	{
		dispatch: AppDispatch;
		state: RootState;
	}
>('app/signalRConnected', async (params, thunkAPI) => {
	thunkAPI.dispatch(setIsSignalRConnected(true));
});

export const joinGroup = createAsyncThunk<
	void,
	{
		hubConnection: HubConnection;
		groupId: string;
	},
	{
		dispatch: AppDispatch;
		state: RootState;
	}
>('app/joinGroup', async (params, thunkAPI) => {
	await params.hubConnection.invoke('JoinGroup', params.groupId);
	thunkAPI.dispatch(setJoinGroup(params.groupId.toString()));
});

export const leaveGroup = createAsyncThunk<
	void,
	{
		hubConnection: HubConnection;
		groupId: string;
	},
	{
		dispatch: AppDispatch;
		state: RootState;
	}
>('app/leaveGroup', async (params, thunkAPI) => {
	await params.hubConnection.invoke('LeaveGroup', params.groupId);
	thunkAPI.dispatch(setLeaveGroup(params.groupId));
});

export const downloadCalculationReports = createAsyncThunk<void, DownloadCalculationReports, { state: RootState; dispatch: AppDispatch }>(
	'globalActions/downloadOutputReports',
	async (params, thunkAPI) => {
		const jobId = thunkAPI.getState().cem?.selectedJob?.id?.toString() ?? params.jobId.toString();
		const response = await downloadReportByCalculationId(params.calculationId.toString(), jobId);
		const fileName = `${currDateAndTime()}_${params.jobId}_Calculation_Reports`;
		if (response.Success) {
			saveStream(response.ContentType ?? null, fileName, response.Data);
			showSuccessSnackbar(thunkAPI.dispatch, Messages.DOWNLOAD_OUTPUT_REPORTS_SUCCESS);
		} else {
			showErrorSnackbar(thunkAPI.dispatch, Messages.DOWNLOAD_OUTPUT_REPORTS_FAILED);
		}
	},
);

export const downloadCalculationAutoReviewReports = createAsyncThunk<void, DownloadCalculationAutoReviewReports, { state: RootState; dispatch: AppDispatch }>(
	'globalActions/downloadAutoReviewOutputReports',
	async (params, thunkAPI) => {
		const response = await downloadAutoReviewReportByCalculationId(params.calculationId.toString(), params.jobId.toString());
		const fileName = `${currDateAndTime()}_${params.jobId}_Calculation_AutoReview_Reports`;
		if (response.Success) {
			saveStream(response.ContentType ?? null, fileName, response.Data);
			showSuccessSnackbar(thunkAPI.dispatch, Messages.DOWNLOAD_AUTOREVIEW_REPORT_SUCCESS);
		} else {
			showErrorSnackbar(thunkAPI.dispatch, Messages.DOWNLOAD_AUTOREVIEW_REPORT_FAILED);
		}
	},
);

export const downloadCalculationErrors = createAsyncThunk<void, Guid, { state: RootState; dispatch: AppDispatch }>(
	'globalActions/downloadCalculationErrors',
	async (calculationId, thunkAPI) => {
		const state = thunkAPI.getState();
		const jobId = state.cem.selectedJob.id.toString();
		const response = await downloadErrorByCalculationId(calculationId.toString(), jobId);
		const fileName = `${currDateAndTime()}_${calculationId}_Calculation_Errors`;
		if (response.Success) {
			saveStream(response.ContentType ?? null, fileName, response.Data);
			showSuccessSnackbar(thunkAPI.dispatch, Messages.DOWNLOAD_CALCULATION_ERROR_SUCCESS);
		} else {
			showErrorSnackbar(thunkAPI.dispatch, Messages.DOWNLOAD_CALCULATION_ERROR_FAILED);
		}
	},
);

export const fetchNotificationsByJobid = createAsyncThunk<
	void,
	string,
	{
		dispatch: AppDispatch;
		state: RootState;
	}
>('job/notification', async (jobId, thunkAPI) => {
	getNotificationsByJobId(jobId)
		.then(async function (result) {
			const state = thunkAPI.getState();
			if (result.Success) {
				const notification = JSON.parse(result.Data.toString()) as PersistentMessage[];
				thunkAPI.dispatch(setPersistentMessage(notification));
			} else {
				showErrorSnackbar(thunkAPI.dispatch, Messages.EXCEPTION_FOR_FETCH)
			}
		})
});

export const fetchNotificationsByUser = createAsyncThunk<
	void,
	void,
	{
		dispatch: AppDispatch;
		state: RootState;
	}
>('job/notification', async (_: void, thunkAPI) => {
	getNotificationsByUser()
		.then(async function (result) {
			if (result.Success) {
				const notification = JSON.parse(result.Data.toString()) as PersistentMessage[];
				thunkAPI.dispatch(setPersistentMessage(notification));
			} else {
				showErrorSnackbar(thunkAPI.dispatch, Messages.EXCEPTION_FOR_FETCH)
			}
		})
});

export const fetchNotificationsByEventGroup = createAsyncThunk<
	void,
	number,
	{
		dispatch: AppDispatch;
		state: RootState;
	}
>('job/notificationByEvent', async (eventGroup: number, thunkAPI) => {
	getNotificationsByEventGroup(eventGroup)
		.then(async function (result) {
			if (result.Success) {
				const notification = JSON.parse(result.Data.toString()) as PersistentMessage[];
				thunkAPI.dispatch(setPersistentMessage(notification));
			} else {
				showErrorSnackbar(thunkAPI.dispatch, Messages.EXCEPTION_FOR_FETCH)
			}
		})
});

const notificationsSlice = createSlice({
	name: 'notifications',
	initialState,
	reducers: {
		removeNotificationDot(state): State {
			return { ...state, isNewNotification: 0 };
		},
		setIsSignalRConnected: (state, action: PayloadAction<boolean>) => {
			state.isSignalRConnected = action.payload;
		},
		setJoinGroup: (state, action: PayloadAction<string>) => {
			let groupState = [...state.currentGroup];
			if (action.payload && !groupState.includes(action.payload?.toString())) {
				groupState.push(action.payload.toLowerCase());
			}
			return { ...state, currentGroup: groupState };
		},
		setLeaveGroup: (state, action: PayloadAction<string>) => {
			let prevGroupState = [...state.currentGroup];
			const newGroupState = prevGroupState.filter((item) => item !== action.payload.toLowerCase());
			return { ...state, currentGroup: newGroupState };
		},
		setIsStandardReportEnabledFlag: (state, action: PayloadAction<setIsStandardReportEnabledPayload>) => {
			if (!state.currentGroup.includes(action.payload?.hierarchyLevelId.toLowerCase())) {
				return state;
			}
			return { ...state, isStandardReportEnabled: action.payload.isStandardReportEnabled };
		},
		setPersistentMessage: (state, action: PayloadAction<PersistentMessage[]>) => {
			return { ...state, persistentMessage: action.payload };
		},
		setNewNotification: (state) => {
			return { ...state, isNewNotification: 1 };
		},
	},
});

export const {
	removeNotificationDot,
	setIsSignalRConnected,
	setJoinGroup,
	setLeaveGroup,
	setIsStandardReportEnabledFlag,
	setPersistentMessage,
	setNewNotification
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
